import React, { Fragment, useCallback, useMemo, useState } from "react";
import { Grid, GridProps, Link, LinkProps } from "@mui/material";
import { COLORS } from "../styles/colors";
import LazylyLoadDialogContents, {
  DialogContent,
} from "./Dialogs/LazyLoadDialogContents";

type CustomLinkProps = LinkProps & {
  component: React.ElementType;
  text: string;
};

/**
 * Holds the Footer Menu Items as a MUI Grid.
 *
 * There are two places in which the footer menu is used:
 *   1. In a main container (e.g. on the Portal's home page)
 *   2. In a drawer
 *
 * Depending on where it is being used, we want different items to be displayed:
 *   - in the portal main content, we simply want "Datenschutz", "Disclaimer", "Nutzungsbedingungen" and "Impressum"
 *   - in the portal drawer, we want the full set with "Anleitung", "Hilfe (FAQ)", and "Kontakt"
 *   - same goes for the webmap drawer
 */
const FooterMenu = ({
  gridContainerProps,
  short,
}: {
  gridContainerProps: GridProps;
  short?: boolean;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState<DialogContent>("kontakt");

  const handleOpenClick = useCallback((key: DialogContent) => {
    setDialogKey(key);
    setDialogOpen(true);
  }, []);

  const linkBaseProps = useMemo(() => {
    return {
      style: { color: COLORS.tuv_r_black_30 },
      component: "button", // override React Router Link behavior
    } as const;
  }, []);

  const links: CustomLinkProps[] = useMemo(() => {
    let links = [
      {
        onClick: () => handleOpenClick("datenschutz"),
        text: "Datenschutz",
      },
      {
        onClick: () => handleOpenClick("disclaimer"),
        text: "Disclaimer",
      },
      {
        onClick: () => handleOpenClick("nutzungsbedinungen"),
        text: "Nutzungsbedingungen",
      },
      {
        onClick: () => handleOpenClick("impressum"),
        text: "Impressum",
      },
    ];

    if (!short) {
      links = [
        {
          text: "Anleitung",
          onClick: () => {},
        },
        {
          text: "Hilfe (FAQ)",
          onClick: () => {},
        },
        {
          text: "Kontakt",
          onClick: () => handleOpenClick("kontakt"),
        },
        ...links,
      ];
    }

    return links.map((link) => {
      return { ...linkBaseProps, ...link };
    });
  }, [short]);

  return (
    <>
      <Grid {...gridContainerProps} spacing={0.5} rowGap={0.1}>
        {links.map((link, i) => {
          const { text, ...linkProps } = link;
          return (
            <Fragment key={`fr-${text}`}>
              <Grid item key={text}>
                <Link {...linkProps}>{text}</Link>
              </Grid>
              {i === links.length - 1 ? (
                ""
              ) : (
                <Grid item key={`div-${text}`}>
                  |
                </Grid>
              )}
            </Fragment>
          );
        })}
      </Grid>
      <LazylyLoadDialogContents
        open={dialogOpen}
        onFinish={() => setDialogOpen(false)}
        dialogKey={dialogKey}
      />
    </>
  );
};

export default FooterMenu;
